import { useHomePageRevamp } from '../use-is-home-page';
import { ProductsSectionLegacy } from './ProductsSection.legacy';
import { useEffect, useRef } from 'react';
import { useLoaderData } from 'react-router';
import { useCountryContext } from '@archipro-website/localisation';
import { RoccoLink } from '@rocco/components/link';
import type { loader as homeLoader } from '~/routes/_app._index';
import { SectionLabel } from '../components/SectionLabel';
import { CategoryPills } from '../components/CategoryPills';
import { ComingSoonPill } from '../components/ComingSoonPill';
import { ScrollReveal } from '../components/ScrollReveal';
import heroImg from '../images/products/hero.jpg';
import stoneImg from '../images/products/tiles/stone.jpg';
import daybedImg from '../images/products/tiles/daybed.jpg';
import leverHandleImg from '../images/products/tiles/lever-handle.jpg';
import loungeChairImg from '../images/products/tiles/lounge-chair.jpg';
import wallLightImg from '../images/products/tiles/wall-light.jpg';
import coffeeTableImg from '../images/products/tiles/coffee-table.jpg';
import sideboardImg from '../images/products/tiles/sideboard.jpg';
import diningChairImg from '../images/products/tiles/dining-chair.jpg';
import { button } from '~/ui-rocco/button-system';
import { SplitHeading } from '../components/SplitHeading';

/**
 * Browse Products + Materials (designer parallax demo, white).
 *
 * An editorial header (eyebrow, giant display heading, categories, description,
 * Coming Soon pill + View all CTA) above an aspect-locked parallax montage: a
 * giant "Products" wordmark that drifts slowly down in the background while
 * eight floating product cut-outs rush up in the foreground. Static (no live
 * product data yet). The 1920 × 2843 frame is percentage-based so the montage
 * scales fluidly from desktop down to mobile.
 */

/**
 * Per-tile placement within the 1920 × 2843 frame (percentages), from the
 * design file's product shots (Figma UD4Km1bkdwwXWJn14rloV5, node 9:106).
 * Each shot is a photograph on its own warm ground, so the tiles take their
 * own size and shape rather than a shared square.
 */
interface ParallaxTile {
    image: string;
    /** Left/top as a percentage of the frame's width/height. */
    left: number;
    top: number;
    /** Width as a percentage of the frame's width. */
    width: number;
    /** The shot's width / height. */
    ratio: string;
    /** Parallax depth — foreground tiles (higher) out-run the background. */
    depth: number;
}

const TILES: ParallaxTile[] = [
    {
        image: stoneImg,
        left: 73.82,
        top: 5.61,
        width: 23.75,
        ratio: '342 / 337',
        depth: 0.9,
    },
    {
        image: daybedImg,
        left: 26.94,
        top: 13.74,
        width: 23.06,
        ratio: '332 / 221',
        depth: 1.15,
    },
    {
        image: leverHandleImg,
        left: 57.71,
        top: 30.53,
        width: 23.61,
        ratio: '340 / 280',
        depth: 0.75,
    },
    {
        image: loungeChairImg,
        left: 2.64,
        top: 38.75,
        width: 23.19,
        ratio: '334 / 333',
        depth: 1.25,
    },
    {
        image: wallLightImg,
        left: 36.88,
        top: 49.88,
        width: 19.72,
        ratio: '284 / 339',
        depth: 0.85,
    },
    {
        image: coffeeTableImg,
        left: 73.82,
        top: 59.13,
        width: 23.75,
        ratio: '342 / 220',
        depth: 1.2,
    },
    {
        image: sideboardImg,
        left: 6.6,
        top: 71.46,
        width: 15.28,
        ratio: '220 / 265',
        depth: 1.0,
    },
    {
        image: diningChairImg,
        left: 45.42,
        top: 76.21,
        width: 17.71,
        ratio: '255 / 304',
        depth: 1.3,
    },
];

/**
 * Demo parallax: one rAF-throttled scroll handler writes transforms straight
 * onto the layers — no layout thrash. The wordmark lags the page (slow
 * background, +0.18); the tiles out-run it (fast foreground, −0.08 × depth).
 * Reduced motion → no transforms, everything sits at its laid-out position.
 */
const useParallax = (frameRef: React.RefObject<HTMLElement | null>) => {
    useEffect(() => {
        const frame = frameRef.current;
        if (!frame) return;
        const reduce = window.matchMedia(
            '(prefers-reduced-motion: reduce)',
        ).matches;
        if (reduce) return;

        const title = frame.querySelector<HTMLElement>('[data-parallax-title]');
        const tiles = Array.from(
            frame.querySelectorAll<HTMLElement>('[data-depth]'),
        );
        let raf = 0;

        const update = () => {
            raf = 0;
            const rect = frame.getBoundingClientRect();
            const scrolled = window.innerHeight - rect.top;
            if (title) {
                title.style.transform = `translate3d(0, ${(
                    scrolled * 0.18
                ).toFixed(1)}px, 0)`;
            }
            for (const tile of tiles) {
                const depth = Number(tile.dataset.depth) || 1;
                tile.style.transform = `translate3d(0, ${(
                    scrolled *
                    -0.08 *
                    depth
                ).toFixed(1)}px, 0)`;
            }
        };

        const onScroll = () => {
            if (!raf) raf = requestAnimationFrame(update);
        };

        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [frameRef]);
};

/** Filled rounded CTA in the header (markets where Products has shipped). */
const ViewAllProductsCTA = () => (
    <RoccoLink to="/products" className={button('primary', 'l', 'w-fit')}>
        View all Products
    </RoccoLink>
);

const ProductsSectionRevamp = () => {
    const { productPopularCategories } = useLoaderData<typeof homeLoader>();
    const frameRef = useRef<HTMLDivElement>(null);
    useParallax(frameRef);

    // US is pre-launch ("Coming Soon - Fall 2026"); AU/NZ link to the live directory.
    const isComingSoon = useCountryContext().byCountry({
        US: true,
        AU: false,
        NZ: false,
    });

    return (
        <section className="w-full overflow-x-clip bg-white text-[#1f1f21] md:bg-[#FDF7EE]">
            {/* Header — on desktop, the other sections' header layout: the
                page gutter, the rule and eyebrow at half width on the right,
                and the same space above and below the heading. */}
            <ScrollReveal className="mx-auto flex max-w-(--ap-page-width) flex-col max-md:gap-12 max-md:px-6 max-md:pt-10 md:gap-[5.8333vw] md:px-[1.927vw] md:pt-[8.33vw] lg:gap-[7rem] lg:px-[2.3124rem] lg:pt-[10rem]">
                <div className="flex flex-col max-md:gap-6 md:gap-[5.8333vw] lg:gap-[7rem]">
                    <div className="flex w-full flex-col gap-[0.885vw] md:ml-auto md:w-[47.5vw] lg:w-[57rem] lg:gap-[1.062rem]">
                        <div className="h-px w-full bg-black/20" />
                        <SectionLabel className="tracking-[-0.01em] max-md:text-[1.75vw] md:text-[1.2503vw]">
                            (03 – Products)
                        </SectionLabel>
                    </div>
                    <h2 className="m-0 font-sans font-normal max-md:text-[11.72vw] max-md:leading-[11.98vw] max-md:tracking-[-0.49vw] md:w-[88.6vw] md:text-[7.8127vw] md:leading-[1.083] md:tracking-[-0.048em] lg:text-[9.375rem]">
                        <SplitHeading
                            first="Browse Products +"
                            second="Materials"
                        />
                    </h2>
                </div>

                <div className="flex max-md:gap-5 md:gap-[24vw] lg:gap-[28.8rem]">
                    <CategoryPills
                        label="Popular categories:"
                        items={productPopularCategories}
                        className="max-md:w-[38%] md:w-[24.3vw] lg:w-[29.16rem]"
                    />
                    <div className="flex flex-col max-md:flex-1 max-md:gap-5 md:w-[43.96vw] md:gap-[3.49vw] lg:w-[52.752rem] lg:gap-[4.188rem]">
                        <p className="m-0 font-sans leading-[1.3] font-normal max-md:text-[3.13vw] md:text-[1.5225vw]">
                            Explore products and materials architects love to
                            use. From cladding and lighting to finishes and
                            more. Our marketplace connects you with top brands
                            ensuring access to the best materials to complete
                            your space.
                        </p>
                        <div className="flex items-center max-md:gap-4 md:gap-[0.5vw]">
                            {isComingSoon ? (
                                <ComingSoonPill />
                            ) : (
                                <ViewAllProductsCTA />
                            )}
                        </div>
                    </div>
                </div>
            </ScrollReveal>

            {/* Feature banner — wide hero image between the header and the montage */}
            <div className="max-md:mt-10 max-md:px-6 md:mt-[8.33vw] md:px-[1.302vw]">
                <img
                    src={heroImg}
                    alt=""
                    aria-hidden="true"
                    className="aspect-[1761/825] w-full rounded-tile object-cover"
                    loading="lazy"
                />
            </div>

            {/*
              Parallax montage — aspect-locked to the 1920 × 2843 demo frame.
              The negative bottom margin pulls the next section up into the dead
              space the parallax opens beneath the tiles. `margin-bottom` %
              resolves against the parent's width, so -25% == 25% of frame width
              at any viewport. This is safe (no tile clip) because the foreground
              tiles drift upward as you scroll, so each tile's bottom has already
              risen clear of the reclaimed band by the time it scrolls into view
              — the lowest tile (chair) clears it by ~0.75% of frame width at its
              tightest point. Tile positions and the parallax motion are untouched.

              Applied on mobile too: the lowest tile's static bottom already
              leaves ~13% of frame width as dead space, and the tiles drift
              further up by the scroll point where the next section rises into
              view, so the reclaim stays clear of them.
            */}
            <div
                ref={frameRef}
                className="relative -mb-[25%] w-full"
                style={{ aspectRatio: '1920 / 2843' }}
            >
                {/* Giant wordmark — slow background, drifts down on scroll */}
                <p
                    data-parallax-title
                    // Blurred behind the product shots on desktop, as the
                    // product category page blurs its title mark.
                    className="absolute left-0 z-0 m-0 w-full text-center font-sans text-[clamp(80px,10.4vw,200px)] font-normal will-change-transform md:text-[clamp(60px,7.8vw,150px)] md:opacity-80 md:blur-[6px]"
                    style={{
                        top: '25%',
                        lineHeight: 1,
                        letterSpacing: '-0.04em',
                    }}
                >
                    Products
                </p>

                {/* Floating product tiles — fast foreground, rush up on scroll */}
                {TILES.map((tile, index) => (
                    <div
                        key={`${tile.image}-${index}`}
                        data-depth={tile.depth}
                        className="absolute z-10 will-change-transform"
                        style={{
                            left: `${tile.left}%`,
                            top: `${tile.top}%`,
                            width: `${tile.width}%`,
                            aspectRatio: tile.ratio,
                        }}
                    >
                        <img
                            src={tile.image}
                            alt=""
                            aria-hidden="true"
                            className="block size-full object-cover"
                            loading="lazy"
                        />
                    </div>
                ))}
            </div>
        </section>
    );
};

/**
 * The homepage's `revamp_2026` design of this section, or its previous one
 * while the flag is off.
 */
export const ProductsSection = () =>
    useHomePageRevamp() ? <ProductsSectionRevamp /> : <ProductsSectionLegacy />;
